import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const TOAST_EVENT = "compliance-sage:toast";

export interface ToastPayload {
  id: string;
  tone: "success" | "error" | "info";
  message: string;
  href?: string;
  actionLabel?: string;
}

/** Fire-and-forget: dispatches a toast for `ToastHost` (mounted once in Layout) to render. */
export function showToast(payload: Omit<ToastPayload, "id">) {
  const toast: ToastPayload = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    ...payload,
  };
  window.dispatchEvent(new CustomEvent<ToastPayload>(TOAST_EVENT, { detail: toast }));
}

const TONE_CLASSES: Record<ToastPayload["tone"], string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  error: "border-red-200 bg-red-50 text-red-900",
  info: "border-slate-200 bg-white text-slate-900",
};

const AUTO_DISMISS_MS = 8_000;

/**
 * Global toast stack, independent of `ReviewProgressPanel`. Anything in the
 * app can call `showToast()` (e.g. a review finishing analysis while the
 * user is on another page) without needing a shared context provider.
 */
export function ToastHost() {
  const [toasts, setToasts] = useState<ToastPayload[]>([]);

  useEffect(() => {
    const handler = (event: Event) => {
      const toast = (event as CustomEvent<ToastPayload>).detail;
      if (!toast) return;
      setToasts((current) => [...current, toast]);
      window.setTimeout(() => {
        setToasts((current) => current.filter((candidate) => candidate.id !== toast.id));
      }, AUTO_DISMISS_MS);
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, []);

  const dismiss = (id: string) => setToasts((current) => current.filter((toast) => toast.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 z-[60] flex w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 flex-col gap-2 sm:left-auto sm:right-5 sm:w-auto sm:translate-x-0">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${TONE_CLASSES[toast.tone]}`}
        >
          <p className="min-w-0 flex-1">{toast.message}</p>
          <div className="flex shrink-0 items-center gap-2">
            {toast.href && (
              <Link
                to={toast.href}
                onClick={() => dismiss(toast.id)}
                className="text-xs font-semibold underline underline-offset-2 hover:no-underline"
              >
                {toast.actionLabel}
              </Link>
            )}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="text-xs text-current opacity-60 hover:opacity-100"
              aria-label="dismiss"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
